import {
  CreateCdfParams,
  RawCdfEntry,
} from 'features/dataLayers/customCdfs/cdfGenerator'
import { useGetCustomCdfUpsertDeleteFns } from 'features/dataLayers/customCdfs/useGetCustomCdfUpsertDeleteFns'
import {
  callEmatCombinedCategoryWorker,
  useEmatCombinedCategoryData,
} from 'features/dataLayers/ematCategoricalHelpers'
import { LossByPercentageWorkerProps } from 'features/dataLayers/generation/coreLayers/wastage/calculateLossByPercentageData.worker'
import { FdeCdfSpecification } from 'features/dataLayers/generation/fdeCdfSpecification'
import { EmatCombinedCategoryWorkerProps } from 'features/dataLayers/generation/workers/calculateEmatCombinedCategoryData.worker'
import { LossWorkerProps } from 'features/dataLayers/generation/workers/calculateLossData.worker'
import { LossPerYearWorkerProps } from 'features/dataLayers/generation/workers/calculateLossPerYearData.worker'
import { RcaWorkerProps } from 'features/dataLayers/generation/workers/calculateRcaData.worker'
import { RemainingLifeWorkerProps } from 'features/dataLayers/generation/workers/calculateRemainingLifeData.worker'
import { ShipDeckPITThicknessWorkerProps } from 'features/dataLayers/generation/workers/calculateShipDeckPITThickness.worker'
import { ThicknessByMeanWorkerProps } from 'features/dataLayers/generation/workers/calculateThicknessByMeanData.worker'
import { TimeSinceLastRepairWorkerProps } from 'features/dataLayers/generation/workers/calculateTimeSinceLastRepairData.worker'
import { TminWorkerProps } from 'features/dataLayers/generation/workers/calculateTminData.worker'
import { useGetForecastComponentConfig } from 'features/fde/componentConfigAPI'
import { getInspectionDate } from 'features/fde/inspectionDateHelpers'
import { RolodexCacheInstance } from 'features/rolodex'
import isEqual from 'lodash/isEqual'
import isNil from 'lodash/isNil'
import { useCallback, useEffect, useMemo } from 'react'
import useSWRImmutable from 'swr/immutable'
import {
  CustomCorrosionRegion,
  ForecastComponentConfig,
  GeckoAssetModel,
  GeckoComponentDataFrame,
  GeckoComponentDataset,
  GeckoEntryBase,
  GeckoInspection,
  GeckoMeasurement,
  GeckoTMinMethod,
  isInspectionThicknessCDF,
  OverrideRegion,
  PredictedInspectionType,
} from 'types'
import { cantorPairing } from 'utils/cantorPairing'
import { DATE_FORMAT } from 'utils/fde/constants'
import { memoizeOnAllParams } from 'utils/memoizeOnAllParams'

import { useGetGenerateDerivedCdfDataFn } from './useGetGenerateDerivedCdfDataFn'

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

/**
 * A hook that generates the data for a derived CDF -- based on the `cdfSpecification` -- in a Web Worker.
 *
 * Note: all parameters (including the pieces of `overrides`) should be memoized, as this functions performs heavy calculations.
 */
export function useGenerateDerivedDataInWebWorker<RETURN = number>(
  cdfSpecification: FdeCdfSpecification,
  assetModel: GeckoAssetModel | undefined,
  baseThicknessCDF: GeckoComponentDataFrame | undefined,
  selectedInspection: GeckoInspection | undefined,
  forecastComponentConfig: ForecastComponentConfig | undefined,
  overrides: ParamOverrides = {},
  keepPreviousData: boolean = false
): {
  array: RETURN[][]
  isLoading: boolean
} {
  const componentId = baseThicknessCDF?.tags.component

  const { baseThicknessValuesAreLoading, calculateDataInWebWorkerFn } =
    useGetGenerateDerivedValuesInWebWorkerFn<RETURN>(
      cdfSpecification,
      componentId,
      assetModel,
      baseThicknessCDF,
      selectedInspection,
      forecastComponentConfig,
      overrides
    )

  // ------------------------------------------ Call Calculate Function ------------------------------------------

  const canCalculateData =
    cdfSpecification.canCreateCdfData(
      assetModel,
      componentId ?? '',
      forecastComponentConfig ?? {},
      selectedInspection
    ) && !isNil(calculateDataInWebWorkerFn)

  const assetModelJson = assetModel?.raw

  const { data, isLoading: rlCalcIsLoading } = useSWRImmutable(
    // key
    [
      cdfSpecification.source.id,
      cdfSpecification.view.id,
      cdfSpecification.tminMethod.id,
      baseThicknessCDF?.data.params,
      baseThicknessCDF?.generation,
      forecastComponentConfig,
      baseThicknessValuesAreLoading,
      overrides.appliedRegions,
      overrides.customCorrosionRegions,
      overrides.manualTminOverride?.toJSON(),
      assetModelJson,
      selectedInspection?.generation,
    ],
    // function
    async () => (canCalculateData ? calculateDataInWebWorkerFn() : undefined),
    // options
    {
      // There is no cheap way to compare CDF data to know if it has changed,
      // and SWR will attempt to use stableHash... which involves iterating over
      // all elements in the returned array. Very slow. For more information,
      // see: https://github.com/vercel/swr/issues/1719
      //
      // Instead, check if the params to the RL calculation have changed.
      compare: (a, b) => isEqual(a?.calcParams, b?.calcParams),
      keepPreviousData,
    }
  )

  // ------------------------------------------ Return ------------------------------------------

  const staticDefaultReturn: RETURN[][] = useMemo(() => [], [])

  return {
    array: data?.array ?? staticDefaultReturn,
    isLoading: rlCalcIsLoading || baseThicknessValuesAreLoading,
  }
}

// ------------------------------------------ Helper Functions ------------------------------------------

export function useGetGenerateDerivedValuesInWebWorkerFn<RETURN = number>(
  cdfSpecification: FdeCdfSpecification,
  componentId: string | undefined,
  assetModel: GeckoAssetModel | undefined,
  baseThicknessCDF: GeckoComponentDataFrame | undefined,
  selectedInspection: GeckoInspection | undefined,
  forecastComponentConfig: ForecastComponentConfig | undefined,
  overrides: ParamOverrides = {}
): {
  baseThicknessValuesAreLoading: boolean
  calculateDataInWebWorkerFn:
    | (() => Promise<{
        calcParams: DerivedCdfWorkerCalculationParams
        array: RETURN[][]
      }>)
    | undefined
} {
  // Ensure that the baseThicknessCDF is a thickness CDF, except for EmatCombinedCategory which can use EMAT CDFs
  const isValidBaseCdf =
    isNil(baseThicknessCDF) ||
    isInspectionThicknessCDF(baseThicknessCDF) ||
    (cdfSpecification === FdeCdfSpecification.EmatCombinedCategory &&
      baseThicknessCDF?.data?.params?.source?.id === 'emat')

  if (!isValidBaseCdf) {
    throw new Error(
      `Invalid base thickness CDF used in remaining life CDF generation: ${baseThicknessCDF.id}`
    )
  }

  const generateDerivedCdfData = useGetGenerateDerivedCdfDataFn()
  const calculateDataInWebWorker = useCallback(
    () =>
      new Promise<{
        calcParams: DerivedCdfWorkerCalculationParams
        array: RETURN[][]
      }>((resolve, reject) => {
        if (isNil(baseThicknessCDF)) {
          reject("Can't calculate remaining life without a base thickness CDF")
          return
        }

        // Assert that the API calls returned valid data
        if (
          isNil(componentId) ||
          isNil(baseThicknessCDF?.tags.inspection) ||
          isNil(baseThicknessCDF?.tags.component) ||
          isNil(selectedInspection) ||
          isNil(assetModel)
        ) {
          reject("Can't calculate remaining life without all required data")
          return
        }

        generateDerivedCdfData<RETURN>({
          cdfSpecification,
          componentId,
          assetModel,
          baseCDF: baseThicknessCDF,
          selectedInspection,
          forecastComponentConfig,
          overrides,
        })
          .then(resolve)
          .catch((e) => {
            console.error(e)
            reject(e)
          })
      }),
    [
      assetModel,
      baseThicknessCDF,
      cdfSpecification,
      componentId,
      forecastComponentConfig,
      generateDerivedCdfData,
      overrides,
      selectedInspection,
    ]
  )

  return {
    baseThicknessValuesAreLoading: false,
    calculateDataInWebWorkerFn: calculateDataInWebWorker,
  }
}

// ========================================== Types ==========================================

export type ParamOverrides = {
  customCorrosionRegions?: CustomCorrosionRegion[]
  appliedRegions?: OverrideRegion[]
  thicknessValues2dArray?: number[][]
  manualTminOverride?: GeckoMeasurement
}

export type DerivedCdfWorkerCalculationParams =
  | LossWorkerProps
  | LossByPercentageWorkerProps
  | LossPerYearWorkerProps
  | TminWorkerProps
  | RcaWorkerProps
  | RemainingLifeWorkerProps
  | TimeSinceLastRepairWorkerProps
  | ThicknessByMeanWorkerProps
  | EmatCombinedCategoryWorkerProps
  | ShipDeckPITThicknessWorkerProps

// ========================================== On-Demand Generation Hooks ==========================================

// Helper to generate CDF ID
function getCdfId(
  cdfSpecification: FdeCdfSpecification,
  referenceCdf: GeckoComponentDataFrame | undefined,
  selectedCDS?: GeckoComponentDataset | undefined,
  predictedDate?: string | undefined
) {
  const thisIdPieceFromSpecs = `${cdfSpecification.source.id}${cdfSpecification.view.id}${cdfSpecification.tminMethod.id}`
  const thisIdPieceFromThicknessCDF =
    `${referenceCdf?.id ?? ''}` +
    `${referenceCdf?.data.params.aggregation.id ?? ''}` +
    `${referenceCdf?.data.params.angle_plot_setting.id ?? ''}` +
    `${referenceCdf?.data.params.algorithm.id ?? ''}` +
    `${referenceCdf?.data.binning.bin_size.width.num ?? ''}` +
    `${referenceCdf?.data.binning.bin_size.height.num ?? ''}` +
    `${selectedCDS?.id ?? ''}`
  const predictedAddition = predictedDate ? `-predicted-${predictedDate}` : ''
  return thisIdPieceFromThicknessCDF + thisIdPieceFromSpecs + predictedAddition
}

export function useHandleEmatOnDemandGeneration(
  assetModel: GeckoAssetModel | undefined,
  componentSlug: string | undefined,
  rolodexCacheInstance: RolodexCacheInstance,
  selectedInspection?: GeckoInspection | undefined,
  componentDataFrames?: readonly GeckoComponentDataFrame[],
  selectedCDS?: GeckoComponentDataset | undefined
) {
  // ----------------------------------- Can we generate the CDF -----------------------------------

  // For EmatCombinedCategory, we need to defer the ID generation until we have the reference CDF
  // Move this inside the filledCdfArgs useMemo where we have access to referenceCdf

  // Get the "configToUse", which is the rolodex config with overrides.configOverrides applied.
  const cdfSpecification = FdeCdfSpecification.EmatCombinedCategory
  const { entries } = useGetForecastComponentConfig(selectedCDS?.id ?? null)
  const configToUse: GeckoEntryBase<ForecastComponentConfig> | undefined =
    useMemo(() => {
      return entries.at(0) as
        | GeckoEntryBase<ForecastComponentConfig>
        | undefined
    }, [entries])

  const canGenerateCdf = cdfSpecification.canCreateCdfData(
    assetModel,
    componentSlug ?? '',
    //only places fcc is used is for api crs, annular rings and 570
    configToUse?.data ?? {},
    selectedInspection
  )

  const { createCdfFn } = useGetCustomCdfUpsertDeleteFns(rolodexCacheInstance)

  // Use the new EMAT hook to get all EMAT-specific data
  const { ematCategoryCdf, differenceFromNominalCdfs, fetchCdf2dArray } =
    useEmatCombinedCategoryData(
      componentDataFrames ?? [],
      selectedCDS,
      selectedInspection,
      componentSlug
    )

  // Get the predicted date if this is a predicted inspection to make the CDF ID unique
  const predictedDate = PredictedInspectionType.includes(
    selectedInspection?.data.inspection_type as PredictedInspectionType
  )
    ? getInspectionDate({ inspection: selectedInspection })?.format(DATE_FORMAT)
    : undefined

  // ------------------------------------------- CDF Generation ----------------------------------------

  // - - - - - - - - - - - - - - - - - - - - - - Generate - - - - - - - - - - - - - - - - - - - - - -

  const memoizedEmatCalculation = useMemo(
    () => memoizeOnAllParams(callEmatCombinedCategoryWorker),
    []
  )

  useEffect(() => {
    if (!canGenerateCdf) return
    if (!ematCategoryCdf) return

    const dfnCdfs = differenceFromNominalCdfs ?? []
    if (!dfnCdfs.length) return

    const baseId = getCdfId(
      cdfSpecification,
      ematCategoryCdf,
      selectedCDS,
      predictedDate
    )

    dfnCdfs
      .toSorted((a, b) => a.id.localeCompare(b.id))
      .forEach((dfnCdf) => {
        const derivedId = `${baseId}-dfn-${dfnCdf.id}`
        const generation = cantorPairing(
          ematCategoryCdf.generation,
          dfnCdf.generation
        )

        const args: CreateCdfParams = {
          source: cdfSpecification.source,
          // Mirror the underlying Difference-from-Nominal CDF so the "Advanced" menu can toggle
          // between EMAT categorical layers the same way it does for DFN (e.g., Minimum/Average/Low 30 Average).
          aggregation: dfnCdf.data.params.aggregation,
          anglePlotSetting: dfnCdf.data.params.angle_plot_setting,
          algorithm: dfnCdf.data.params.algorithm,
          tminMethod: cdfSpecification.tminMethod,
          binning: dfnCdf.data.binning,
          geometry: dfnCdf.data.geometry,
          tags: {
            ...ematCategoryCdf.tags,
            inspection:
              selectedInspection?.id ?? ematCategoryCdf.tags.inspection,
          },
          view: cdfSpecification.view,
          measurementUnit: cdfSpecification.defaultMeasurementUnit,
          generation,
          data: async () => {
            const [ematCategoryData, differenceFromNominalData] =
              await Promise.all([
                fetchCdf2dArray(ematCategoryCdf),
                fetchCdf2dArray(dfnCdf),
              ])

            if (ematCategoryData && differenceFromNominalData) {
              return memoizedEmatCalculation(
                ematCategoryCdf,
                selectedInspection,
                assetModel,
                configToUse?.data,
                componentSlug,
                ematCategoryData,
                differenceFromNominalData
              )
            }

            return []
          },
        }
        createCdfFn(derivedId, args)
      })
  }, [
    assetModel,
    canGenerateCdf,
    cdfSpecification,
    componentSlug,
    configToUse?.data,
    configToUse?.generation,
    configToUse?.id,
    createCdfFn,
    differenceFromNominalCdfs,
    ematCategoryCdf,
    fetchCdf2dArray,
    memoizedEmatCalculation,
    predictedDate,
    selectedCDS,
    selectedInspection,
  ])
}

type GenerateAndUploadDerivedCdfFn = (
  cdfSpecification: FdeCdfSpecification,
  baseCDF: GeckoComponentDataFrame,
  assetModel: GeckoAssetModel,
  forecastComponentConfig: ForecastComponentConfig | undefined,
  baseInspection: GeckoInspection | undefined,
  generationNumberOverride?: number,
  // For predicted CDFs only: the real inspection used as the data source for calculations. Indicates we're generating a predicted layer.
  realInspectionForPredictionCalculation?: GeckoInspection
) => RawCdfEntry
export function useGetGenerateAndUploadDerivedCdfFn(
  rolodexCacheInstance: RolodexCacheInstance
): GenerateAndUploadDerivedCdfFn {
  const calculateDataInWebWorker = useGetGenerateDerivedCdfDataFn()
  const { createCdfFn } = useGetCustomCdfUpsertDeleteFns(rolodexCacheInstance)

  const generateAndUploadDerivedCdfFn =
    useCallback<GenerateAndUploadDerivedCdfFn>(
      (
        cdfSpecification,
        baseCDF,
        assetModel,
        forecastComponentConfig,
        baseInspection,
        generationNumberOverride,
        realInspectionForPredictionCalculation
      ) => {
        // When realInspectionForPredictionCalculation is provided, we're generating a predicted CDF
        const isPredicted = !!realInspectionForPredictionCalculation

        const predictedDate = isPredicted
          ? getInspectionDate({ inspection: baseInspection })?.format(
              DATE_FORMAT
            )
          : undefined

        const realInspectionDateForPredictionCalculation = getInspectionDate({
          inspection: realInspectionForPredictionCalculation,
        })?.format(DATE_FORMAT)

        const thisCdfId = getCdfId(
          cdfSpecification,
          baseCDF,
          undefined,
          predictedDate
        )
        const cdfToGenerateParams: CreateCdfParams = {
          source: cdfSpecification.source,
          aggregation:
            cdfSpecification.aggregation ?? baseCDF.data.params.aggregation,
          anglePlotSetting: baseCDF.data.params.angle_plot_setting,
          algorithm: baseCDF.data.params.algorithm,
          tminMethod:
            cdfSpecification.tminMethod.id === GeckoTMinMethod.None.id
              ? undefined
              : cdfSpecification.tminMethod,
          binning: baseCDF.data.binning,
          geometry: baseCDF.data.geometry,
          tags: {
            ...baseCDF.tags,
            inspection: baseInspection?.id ?? baseCDF.tags.inspection,
          },
          view: cdfSpecification.view,
          measurementUnit: cdfSpecification.defaultMeasurementUnit,
          generation: generationNumberOverride,
          data: async () =>
            (
              await calculateDataInWebWorker({
                cdfSpecification: cdfSpecification,
                componentId: baseCDF.tags.component,
                assetModel: assetModel,
                baseCDF: baseCDF,
                selectedInspection: baseInspection,
                forecastComponentConfig: forecastComponentConfig,
                realInspectionDateForPredictionCalculation:
                  realInspectionDateForPredictionCalculation,
              })
            ).array,
        }
        return createCdfFn(thisCdfId, cdfToGenerateParams)
      },
      [calculateDataInWebWorker, createCdfFn]
    )

  return useMemo(() => {
    const memoizedFn = memoizeOnAllParams(generateAndUploadDerivedCdfFn)

    return function (
      ...params: Parameters<typeof generateAndUploadDerivedCdfFn>
    ) {
      return memoizedFn(...params)
    }
  }, [generateAndUploadDerivedCdfFn])
}

// ---

/**
 * A hook that returns a function that takes in a list of CDF specifications and base values for cdf-generation,
 * generates all possible specified CDFs, and returns the ones that were successfully generated.
 * @param rolodexCacheInstance
 */
export function useGetGenerateAllValidSpecifiedCdfsFromBaseFn(
  rolodexCacheInstance: RolodexCacheInstance
) {
  const generateAndUploadDerivedCdfFn =
    useGetGenerateAndUploadDerivedCdfFn(rolodexCacheInstance)

  return useCallback(
    (props: {
      cdfSpecifications: ReadonlyArray<FdeCdfSpecification>
      baseCDF: GeckoComponentDataFrame
      assetModel: GeckoAssetModel
      forecastComponentConfig: ForecastComponentConfig | undefined
      baseInspection: GeckoInspection | undefined
    }): ReadonlyArray<FdeCdfSpecification> => {
      return props.cdfSpecifications
        .map((spec) => {
          const canGenerateCdf = spec.canCreateCdfData(
            props.assetModel,
            props.baseCDF.tags.component,
            //only places fcc is used is for api crs, annular rings and 570
            props.forecastComponentConfig ?? {},
            props.baseInspection
          )

          if (!canGenerateCdf) return null

          generateAndUploadDerivedCdfFn(
            spec,
            props.baseCDF,
            props.assetModel,
            props.forecastComponentConfig,
            props.baseInspection
          )

          return spec
        })
        .filter((s) => !isNil(s))
    },
    [generateAndUploadDerivedCdfFn]
  )
}
